import {
  EmptyState,
  Table,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { Chip } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useAppConfigProfiles } from '../hooks/useAppConfigProfiles';
import { appConfigApiRef } from '../api/appconfig-api';

type FlagType = 'boolean' | 'variant' | 'attribute';

type FlagRow = {
  application: string;
  configuration: string;
  region: string;
  name: string;
  description?: string;
  enabled: boolean;
  flagType: FlagType;
  variantValue?: string;
  attributeKeys?: string[];
  applicationId: string;
  profileId: string;
};

const columns: TableColumn<FlagRow>[] = [
  {
    title: 'Application',
    field: 'application',
  },
  {
    title: 'Configuration',
    field: 'configuration',
  },
  {
    title: 'Region',
    field: 'region',
  },
  {
    title: 'Flag',
    field: 'name',
    cellStyle: { whiteSpace: 'nowrap' },
    render: row => (
      <div>
        <div>{row.name}</div>
        <div style={{ fontSize: '0.75rem', marginTop: 2 }}>
          {row.description ?? 'No description'}
        </div>
      </div>
    ),
  },
  {
    title: 'Enabled',
    field: 'enabled',
    cellStyle: { whiteSpace: 'nowrap' },
    render: row => {
      if (row.flagType === 'variant') {
        return (
          <Chip
            label={`Variant: ${row.variantValue}`}
            size="small"
            color="secondary"
          />
        );
      }

      if (row.flagType === 'attribute') {
        return (
          <Chip
            label={row.enabled ? 'Attribute · Enabled' : 'Attribute · Disabled'}
            size="small"
            color={row.enabled ? 'primary' : 'default'}
          />
        );
      }

      return (
        <Chip
          label={row.enabled ? 'Enabled' : 'Disabled'}
          size="small"
          color={row.enabled ? 'primary' : 'default'}
        />
      );
    },
    customFilterAndSearch: (term, row) => {
      const t = term.toLowerCase();
      if (row.flagType === 'variant') {
        return `variant: ${row.variantValue ?? ''}`.toLowerCase().includes(t);
      }
      if (row.flagType === 'attribute') {
        return `attribute ${row.enabled ? 'enabled' : 'disabled'}`.includes(t);
      }
      return (row.enabled ? 'enabled' : 'disabled').includes(t);
    },
  },
];

function parseFlagRows(
  application: string,
  configuration: string,
  region: string,
  rawConfig: unknown,
  applicationId: string,
  profileId: string,
): FlagRow[] {
  if (!rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) {
    return [];
  }

  const raw = rawConfig as Record<string, unknown>;

  const values =
    raw.values && typeof raw.values === 'object' && !Array.isArray(raw.values)
      ? (raw.values as Record<string, Record<string, unknown>>)
      : raw;

  const flags =
    raw.flags && typeof raw.flags === 'object' && !Array.isArray(raw.flags)
      ? (raw.flags as Record<string, Record<string, unknown>>)
      : {};

  return Object.entries(values)
    .filter(([k]) => !k.startsWith('_'))
    .map(([key, attrs]) => {
      const attributes =
        attrs && typeof attrs === 'object' && !Array.isArray(attrs)
          ? (attrs as Record<string, unknown>)
          : {};
      const flagMeta = flags[key] ?? {};
      const attrKeys = Object.keys(attributes).filter(
        k => !k.startsWith('_') && k !== 'enabled',
      );
      const isVariant = '_variant' in attributes;
      let flagType: FlagType = 'boolean';
      if (isVariant) {
        flagType = 'variant';
      } else if (attrKeys.length > 0) {
        flagType = 'attribute';
      }
      const name = typeof flagMeta.name === 'string' ? flagMeta.name : key;

      return {
        application,
        configuration,
        region,
        name,
        description:
          typeof flagMeta.description === 'string'
            ? flagMeta.description
            : undefined,
        enabled: Boolean(attributes.enabled),
        flagType,
        variantValue: isVariant ? String(attributes._variant ?? '') : undefined,
        attributeKeys: flagType === 'attribute' ? attrKeys : undefined,
        applicationId,
        profileId,
      };
    });
}

function buildConsoleUrl(
  row: FlagRow,
  accountId: string,
  ssoDomain: string,
): string {
  const region = row.region || 'us-east-1';
  const destination = encodeURIComponent(
    `https://${region}.console.aws.amazon.com/systems-manager/appconfig/applications/${encodeURIComponent(
      row.applicationId,
    )}/featureflags/${encodeURIComponent(
      row.profileId,
    )}?region=${encodeURIComponent(region)}&tab=config`,
  );
  return `https://${ssoDomain}.awsapps.com/start/#/console?account_id=${encodeURIComponent(
    accountId,
  )}&role_name=pfg-readonly&destination=${destination}`;
}

export interface AppConfigProfilesCardProps {
  actions?: TableProps<FlagRow>['actions'];
}

export const AppConfigProfilesCard = ({
  actions,
}: AppConfigProfilesCardProps) => {
  const { profiles, loading: profilesLoading } = useAppConfigProfiles();
  const { entity } = useEntity();
  const appConfigApi = useApi(appConfigApiRef);
  const configApi = useApi(configApiRef);
  const ssoDomain = configApi.getString('aws.console.domain');
  const accountId =
    entity.metadata.annotations?.['amazonaws.com/account-id'] ?? '';
  const environment =
    (entity.metadata.labels as Record<string, string> | undefined)?.[
      'pfg-env-type'
    ] ?? '';

  const { value: rows = [], loading: configLoading } = useAsync(async () => {
    if (profilesLoading || !accountId) return [];
    if (!environment) return [];

    const flagProfiles = profiles
      .filter(p => p.type === 'AWS.AppConfig.FeatureFlags' && p.applicationId)
      .map(p => ({ ...p }));

    if (!flagProfiles.length) return [];

    const results = await Promise.all(
      flagProfiles.map(p =>
        appConfigApi
          .getConfiguration(
            accountId,
            p.applicationId,
            environment,
            p.id,
            p.region,
          )
          .catch((e: unknown) => ({
            error: e instanceof Error ? e : new Error(String(e)),
          })),
      ),
    );

    const firstError = results.find(
      (r): r is { error: Error } => r !== null && 'error' in (r as object),
    );
    if (
      firstError &&
      results.every(r => r !== null && 'error' in (r as object))
    ) {
      throw firstError.error;
    }

    return results
      .flatMap((result, i) => {
        if (result === null || 'error' in (result as object)) return [];
        return parseFlagRows(
          flagProfiles[i].applicationName ?? flagProfiles[i].applicationId,
          flagProfiles[i].name,
          flagProfiles[i].region ?? '',
          (result as { configuration: unknown }).configuration,
          flagProfiles[i].applicationId,
          flagProfiles[i].id,
        );
      })
      .sort((a, b) => {
        const appCmp = a.application.localeCompare(b.application);
        if (appCmp !== 0) return appCmp;
        const confCmp = a.configuration.localeCompare(b.configuration);
        if (confCmp !== 0) return confCmp;
        return a.name.localeCompare(b.name);
      });
  }, [profiles, accountId, environment]);

  const loading = profilesLoading || configLoading;

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No feature flags found for this account."
      />
    );
  }

  return (
    <Table<FlagRow>
      title="Feature Flags"
      isLoading={loading}
      options={{
        search: true,
        paging: true,
        pageSize: 10,
        toolbar: true,
        actionsColumnIndex: -1,
      }}
      columns={columns}
      data={rows}
      actions={[
        {
          icon: () => <OpenInNewIcon fontSize="small" />,
          tooltip: 'View in AWS Console',
          onClick: (_event, row) => {
            const r = row as FlagRow;
            window.open(
              buildConsoleUrl(r, accountId, ssoDomain),
              '_blank',
              'noopener,noreferrer',
            );
          },
        },
        ...(actions ?? []),
      ]}
    />
  );
};
